import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axios from "../utils/api";
import Swal from "sweetalert2";
import logo from "../assets/shoe.png";
import bgImage from "../assets/airmax.webp";

const fmtKesh = (n) =>
  `Kesh${Number(n || 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const todayISO = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const AddressBlock = ({ shopId }) => {
  const id = Number(shopId);
  if (id === 1) {
    return (
      <>
        <p><strong>NAIROBI, KENYA</strong></p>
        <p>Kamukunji Market</p>
        <p>Building: Marpet Plaza, First Floor, Shop No. 20</p>
        <p>Contact: +2540740357257</p>
      </>
    );
  }
  if (id === 2) {
    return (
      <>
        <p><strong>NAIROBI, KENYA</strong></p>
        <p>Kamukunji Market</p>
        <p>Building: Marpet Plaza, Second Floor, Shop No. 46</p>
        <p>Contact: +2540740357257</p>
      </>
    );
  }
  return <p className="text-red-500">⚠️ location info unavailable</p>;
};

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ id: null, product_id: "", quantity: "", unit: "" });
  const [loading, setLoading] = useState(false);

  // header (from stock-tracking endpoint)
  const [shopMeta, setShopMeta] = useState({ shop_id: null, shop_name: "" });

  // --------- load header same as StockTracking ---------
  const loadShopMeta = async () => {
    try {
      const res = await axios.get("/stock-tracking.php", { params: { date: todayISO() } });
      if (res.data?.shop_id) {
        setShopMeta({
          shop_id: res.data.shop_id,
          shop_name: res.data.shop_name || `PETRAH COLLECTION - SHOP #${res.data.shop_id}`,
        });
      } else {
        setShopMeta({ shop_id: null, shop_name: "PETRAH COLLECTION - SHOP #?" });
      }
    } catch {
      setShopMeta({ shop_id: null, shop_name: "PETRAH COLLECTION - SHOP #?" });
    }
  };

  // --------- data fetchers ---------
  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/inventory.php");
      setInventory(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to fetch inventory", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get("/products.php");
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to fetch products", "error");
    }
  };

  useEffect(() => {
    loadShopMeta();
    fetchInventory();
    fetchProducts();
  }, []);

  // --------- form handlers ---------
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleProductChange = (opt) => setForm({ ...form, product_id: opt ? opt.value : "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      id: form.id,
      product_id: form.product_id,
      quantity: parseInt(form.quantity, 10),
      unit: form.unit,
    };
    try {
      if (form.id) {
        await axios.put("/inventory.php", payload);
        Swal.fire("✅ Inventory updated!", "", "success");
      } else {
        await axios.post("/inventory.php", payload);
        Swal.fire("✅ Stock added!", "", "success");
      }
      setForm({ id: null, product_id: "", quantity: "", unit: "" });
      fetchInventory();
    } catch (err) {
      console.error(err);
      Swal.fire("❌ Failed", "Something went wrong", "error");
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      product_id: item.product_id,
      quantity: item.quantity,
      unit: item.unit,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --------- UI data ---------
  const productOptions = products.map((p) => ({
    value: p.id,
    label: `${p.name}${p.size ? ` (${p.size})` : ""}${p.color ? ` - ${p.color}` : ""}`,
  }));
  const selectedProduct =
    productOptions.find((opt) => opt.value === form.product_id) || null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inventory;
    return inventory.filter((item) =>
      `${item.name} ${item.size} ${item.color} ${item.unit}`.toLowerCase().includes(q)
    );
  }, [inventory, search]);

  const handlePrint = () => window.print();

  return (
    <div className="w-full h-full relative overflow-y-auto">
      {/* Background (hidden on print) */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 blur-sm print:hidden"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
    <style>{`
/* =====================================================
   PRINT MODE — MULTI-PAGE + HEADER KILL (FINAL + PAGE NUMBERS)
===================================================== */
@media print {

  /* =====================================================
     1. FORCE-KILL ADMIN / FIXED HEADERS (CRITICAL)
  ===================================================== */
  header,
  nav,
  [role="banner"],
  .petrah-admin,
  .admin-header,
  .topbar,
  .navbar,
  .sidebar,
  .layout-header,
  .app-header,
  .fixed,
  .sticky,
  .is-fixed,
  .is-sticky {
    display: none !important;
    visibility: hidden !important;
    position: static !important;
    height: 0 !important;
    max-height: 0 !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
  }

  /* 🚨 ABSOLUTE OVERRIDE — FIXED ELEMENTS NEVER PRINT */
  body * {
    position: static !important;
  }

  /* =====================================================
     2. REMOVE SCROLL / HEIGHT LIMITS (MULTI-PAGE FIX)
  ===================================================== */
  *,
  *::before,
  *::after {
    overflow: visible !important;
    max-height: none !important;
    height: auto !important;
  }

  /* =====================================================
     3. RESET DOCUMENT
  ===================================================== */
  html,
  body {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* =====================================================
     4. FULL WIDTH CONTENT
  ===================================================== */
  main,
  .main,
  .content,
  .page-content,
  .app-content,
  .container,
  .card {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
  }

  /* =====================================================
     5. PAGE SETUP + PAGE NUMBERS ✅
  ===================================================== */
  @page {
    size: A4;
    margin: 12mm;

    /* ---- FOOTER PAGE NUMBERS ---- */
    @bottom-center {
      content: "Page " counter(page) " of " counter(pages);
      font-size: 10px;
      color: #555;
    }
  }

  /* =====================================================
     6. HIDE NON-PRINT UI
  ===================================================== */
  .no-print,
  .print-hidden,
  button,
  input,
  select,
  textarea {
    display: none !important;
  }

  /* =====================================================
     7. REMOVE VISUAL EFFECTS
  ===================================================== */
  * {
    box-shadow: none !important;
    text-shadow: none !important;
    filter: none !important;
    backdrop-filter: none !important;
  }

  /* =====================================================
     8. TABLE — MULTI-PAGE SAFE
  ===================================================== */
  table {
    width: 100% !important;
    border-collapse: collapse !important;
    page-break-inside: auto !important;
  }

  thead {
    display: table-header-group;
  }

  tbody {
    display: table-row-group;
  }

  tr {
    page-break-inside: avoid !important;
    page-break-after: auto !important;
  }

  th,
  td {
    border: 1px solid #ddd !important;
    padding: 6px !important;
    font-size: 11px !important;
    line-height: 1.4 !important;
    vertical-align: top !important;
  }

  th {
    background: #f3f4f6 !important;
    font-weight: 600 !important;
  }
}
`}</style>



      <div className="relative z-10 p-6 overflow-visible">
        {/* ===== Header (prints) ===== */}
        <div className="bg-white rounded-xl shadow card p-6 max-w-5xl mx-auto mb-6">
          <div className="flex items-start justify-between">
            {/* Left: logo + shop name */}
            <div className="flex items-start gap-3">
              <img src={logo} alt="logo" className="h-12 w-12" />
              <div>
                <h1 className="text-lg font-bold uppercase">
                  {shopMeta.shop_name || "PETRAH COLLECTION"}
                </h1>
                <p className="text-sm text-gray-500">
                  shop id: {shopMeta.shop_id ?? "—"}
                </p>
              </div>
            </div>
            {/* Right: address */}
            <div className="text-right text-sm">
              <AddressBlock shopId={shopMeta.shop_id} />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mt-4">
            <h2 className="text-xl font-semibold">Current Inventory</h2>
            <p className="text-xs text-gray-500">
              generated on {new Date().toISOString().replace("T", " ").slice(0, 19)}
            </p>
          </div>

          {/* Print (hidden on print) */}
          <div className="mt-4 flex justify-end gap-2 no-print">
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              🖨️ Print / Save PDF
            </button>
          </div>
        </div>

        {/* ===== Form (no print) ===== */}
        <div className="bg-white/90 backdrop-blur-md rounded-xl shadow card p-6 max-w-5xl mx-auto mb-8 no-print">
          <h2 className="text-2xl font-semibold text-gray-700 mb-6 flex items-center gap-2">
            📥 {form.id ? "Edit Inventory Entry" : "Add Stock to Inventory"}
          </h2>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="hidden" name="id" value={form.id || ""} />

            <Select
              options={productOptions}
              value={selectedProduct}
              onChange={handleProductChange}
              placeholder="🔍 Search & Select Product"
              isClearable
              className="text-sm z-[9999]"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            <input
              type="number"
              name="quantity"
              placeholder="Quantity"
              value={form.quantity}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 outline-none"
              required
            />

            <select
              name="unit"
              value={form.unit}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 outline-none"
              required
            >
              <option value="">-- Select Unit --</option>
              <option value="pair">Pair</option>
              <option value="box">Pce</option>
              <option value="dozen">Packet</option>
              <option value="unit">Unit</option>
            </select>

            <div className="col-span-1 md:col-span-3">
              <button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-md transition"
              >
                {form.id ? "✏️ Update Stock" : "➕ Add Stock"}
              </button>
            </div>
          </form>
        </div>

        {/* ===== Table (printable; Action hidden) ===== */}
        <div className="bg-white/90 backdrop-blur-md rounded-xl shadow card p-6 max-w-5xl mx-auto">
          {/* Search (no print) */}
          <input
            type="text"
            placeholder="🔍 Search inventory..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md no-print"
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border border-gray-200">
              <thead className="bg-gray-100 text-gray-700 font-medium">
  <tr>
    <th className="px-3 py-3 w-10 text-center">#</th>
    <th className="px-4 py-3">Product</th>
    <th className="px-4 py-3">Size</th>
    <th className="px-4 py-3">Color</th>
    <th className="px-4 py-3">Price</th>
    <th className="px-4 py-3">Quantity</th>
    <th className="px-4 py-3">Unit</th>
    <th className="px-4 py-3 no-print">Action</th>
  </tr>
</thead>

              <tbody className="text-gray-700">
  {filtered.map((item, index) => (
    <tr key={item.id} className="border-t hover:bg-gray-50">
      <td className="px-3 py-2 text-center font-medium">
        {index + 1}
      </td>
      <td className="px-4 py-2">{item.name}</td>
      <td className="px-4 py-2">{item.size}</td>
      <td className="px-4 py-2">{item.color}</td>
      <td className="px-4 py-2">{fmtKesh(item.price)}</td>
      <td className="px-4 py-2">{item.quantity}</td>
      <td className="px-4 py-2 capitalize">{item.unit || "unit"}</td>
      <td className="px-4 py-2 no-print">
        <button
          onClick={() => handleEdit(item)}
          className="text-green-600 hover:underline"
        >
          Edit
        </button>
      </td>
    </tr>
  ))}
</tbody>

            </table>
          </div>

          {/* Totals */}
          {filtered.length > 0 && (
            <div className="mt-4 flex justify-end text-sm">
              <div className="text-right">
                <p><span className="font-semibold">Total items:</span> {filtered.length}</p>
                <p>
                  <span className="font-semibold">Total quantity:</span>{" "}
                  {filtered.reduce((s, r) => s + Number(r.quantity || 0), 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inventory;
